import React, { useState } from 'react';
import { Calendar } from 'react-date-range';
import { useNavigate } from 'react-router-dom';

import { Popover } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import moment from 'moment-timezone';

import { createEntityAppointment, rescheduleAppointmentRecurrence } from 'api/Appointment';

import { formatDate } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import Input from 'components/shared/Input/Input';
import Confirm from 'components/shared/Modal/Confirm/Confirm';

const AppointmentDate = ({ timezone, className }) => {
  const {
    values,
    setFieldValue,
    handleSubmit,
    setAppointmentId,
    handleCloseEncounterModal,
    initialValues
  } = useFormikContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const isAptDisabled = values.appointment_disabled;
  const formatDateForInput = (date, timezone) =>
    moment.tz(date, timezone).format('MM/DD/YYYY, dddd');

  const handleCreateEntity = async (params) => {
    const { code, updatedAppointment } = await createEntityAppointment(navigate, params);

    if (code === 0) {
      queryClient.invalidateQueries(['appointment-events']);
      showAlert({ color: 'success', message: 'Appointment updated successfully!' });
      setAppointmentId(updatedAppointment?.id);
    }

    if (code === 6) {
      showAlert({
        message: 'An appointment already exists at this time',
        color: 'warning',
        buttons: [
          {
            text: 'Reschedule anyway',
            onClick: () => handleCreateEntity({ ...params, isReschedule: false }),
            color: 'warning'
          }
        ]
      });
    }
  };

  const handleDateChange = async (date, type = null) => {
    const isRecurrence = !!values.isRecurrence;

    if (isRecurrence && !type) {
      setIsModalOpen(true);
      setSelectedDate(date);
      return;
    }

    const starts_at = moment(values?.starts_at).format('YYYY-MM-DDTHH:mm');
    const ends_at = moment(values?.ends_at).format('YYYY-MM-DDTHH:mm');
    const diffMin = moment(ends_at).diff(moment(starts_at), 'minutes');

    const dt = moment.tz(moment(date).add(12, 'h'), timezone).format('L');
    const time = moment.tz(values.starts_at, timezone).format('h:mm A') || '';
    const newTime = moment.tz(dt + ' ' + time, 'M/D/YYYY h:mm A z', timezone);

    if (type === 'only' && !values.parent_appointment_id) {
      setIsLoading(true);

      const params = {
        appointment_id: values.id,
        appointment_date: {
          starts_at: moment.tz(newTime, timezone).format(),
          ends_at: moment.tz(newTime, timezone).add(diffMin, 'm').format(),
          deleted_date: formatDate(initialValues.starts_at)
        },
        isReschedule: true
      };
      await handleCreateEntity(params);
      setTimeout(() => {
        queryClient.invalidateQueries(['appointment-events']);
      }, 1000);
      setIsLoading(false);
      return;
    }

    if (type === 'all') {
      const params = {
        appointmentChanges: {
          id: values.id,
          starts_at: moment.tz(newTime, timezone).format(),
          ends_at: moment.tz(newTime, timezone).add(diffMin, 'm').format()
        },
        recurrence: {
          base_end_date: moment(values.starts_at).subtract(1, 'day').toDate(),
          base_appointment: values?.is_base_appointment
        }
      };
      const { code } = await rescheduleAppointmentRecurrence(navigate, params);

      if (code === 0) {
        setTimeout(() => {
          queryClient.invalidateQueries(['appointment-events']);
        }, 1000);
        handleCloseEncounterModal();
        showAlert({ color: 'success', message: 'Appointment updated successfully!' });
      }
      setIsLoading(false);
      return;
    }

    if (values.parent_appointment_id) {
      setFieldValue('starts_at', moment.tz(newTime, timezone).format());
      setFieldValue('ends_at', moment.tz(newTime, timezone).add(diffMin, 'm').format()).then(() => {
        handleSubmit();
        setIsLoading(false);
        setIsModalOpen(false);
        handleCloseEncounterModal();
      });
      return;
    }

    setFieldValue('starts_at', moment.tz(newTime, timezone).format());
    setFieldValue('ends_at', moment.tz(newTime, timezone).add(diffMin, 'm').format());
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };
  return (
    <>
      <Popover className={className}>
        {() => (
          <div className="relative">
            <Popover.Button className="w-full !p-0" disabled={isAptDisabled}>
              <Input
                label="Appointment date"
                placeholder="Please select date"
                rightIcon="calendar-mono"
                value={formatDateForInput(values.starts_at, timezone)}
                data-qa="appointment-date-input"
              />
            </Popover.Button>

            <Popover.Panel className="absolute right-0 top-[110%] z-[100] flex w-max bg-white shadow-md">
              <Calendar
                date={new Date(values.starts_at)}
                onChange={(date) => handleDateChange(date)}
                data-qa="appointment-date-calendar"
              />
            </Popover.Panel>
          </div>
        )}
      </Popover>
      {isModalOpen && moment(selectedDate).isAfter(moment(new Date())) ? (
        <Confirm
          title="You're changing a repeating appointment!"
          message={`You're changing the date of a repeating appointment. Do you want to move only this recurrence to ${formatDate(selectedDate)}, or change the date for this and all future appointments?`}
          handleOpen={isModalOpen}
          customClassName="w-[720px]"
          icon="calendar-dates"
          footer={
            <div className="flex items-center justify-between pt-4">
              <Button size="small" outlined text="Cancel" color="neutral" onClick={handleClose} />
              <div className="flex items-center gap-2">
                <Button
                  size="small"
                  text="Only this appointment"
                  color="primary"
                  onClick={() => handleDateChange(selectedDate, 'only')}
                  loading={isLoading}
                  disabled={isLoading}
                />
                <Button
                  size="small"
                  outlined
                  text="All future appointments"
                  color="primary"
                  onClick={() => handleDateChange(selectedDate, 'all')}
                  loading={isLoading}
                  disabled={isLoading}
                />
              </div>
            </div>
          }
          handleClose={handleClose}
        />
      ) : (
        <Confirm
          title="You're date of a repeating appointment in the past!"
          message="You are changing the date of a repeating appointment to a date in the past. Do you want to move only this recurrence to the selected date?"
          handleOpen={isModalOpen}
          customClassName="w-[720px]"
          icon="calendar-dates"
          footer={
            <div className="flex items-center justify-between pt-4">
              <Button size="small" outlined text="Cancel" color="neutral" onClick={handleClose} />
              <Button
                size="small"
                text="Reschedule appointment"
                color="primary"
                onClick={() => handleDateChange(selectedDate, 'only')}
                loading={isLoading}
                disabled={isLoading}
              />
            </div>
          }
          handleClose={handleClose}
        />
      )}
    </>
  );
};

export default AppointmentDate;
